import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-8db041f8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "has-text-centered is-flex is-align-items-center is-flex-direction-column my-6 px-4" }
const _hoisted_2 = { class: "profile-wrapper p-4" }
const _hoisted_3 = {
  class: "mx-auto mt-2",
  "data-testid": "profile-name"
}
const _hoisted_4 = { class: "message-wrapper is-flex is-flex-direction-column p-4" }
const _hoisted_5 = { class: "is-flex is-justify-content-space-between" }
const _hoisted_6 = { class: "tag" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("lukso-icon", {
  class: "mr-2",
  name: "mail",
  color: "neutral-20",
  size: "small"
}, null, -1 /* HOISTED */))
const _hoisted_8 = { class: "is-clickable tag siwe-tag" }
const _hoisted_9 = {
  class: "sign-message is-flex-grow-2 px-4 pt-4",
  "data-testid": "message"
}
const _hoisted_10 = {
  key: 0,
  class: "sign-resources mt-6 px-4"
}
const _hoisted_11 = { class: "sign-resources-header mb-2" }
const _hoisted_12 = ["onClick", "title"]
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("lukso-icon", {
  class: "mr-2",
  name: "link",
  color: "neutral-20",
  size: "small"
}, null, -1 /* HOISTED */))

import { onMounted, onUnmounted, ref } from 'vue'
import browser from 'webextension-polyfill'
import { SiweMessage } from 'siwe'

import { useAppStore } from '@/popup/store/AppStore'
import UserName from '@/popup/components/shared/Miscellaneous/UserName.vue'
import IconButton from '@/popup/components/shared/IconButton.vue'
import Button from '@/popup/components/shared/Button.vue'
import ProfilePicture from '@/popup/components/shared/ProfilePicture/ProfilePicture.vue'
import { truncateMiddle, removeSchemaFromUrl } from '@/shared/utils'
import Sanitize from '@/popup/components/shared/Sanitize.vue'
import Notification from '@/popup/components/shared/Notification.vue'
import {
  validateSiweMessage,
  SiweError,
  parseSiweMessage,
  validateSiweExpirationTime,
  validateSiweReadyTime,
} from '@/popup/utils/siweMessage'
import ApprovalHeader from '@/popup/components/shared/ApprovalHeader.vue'
import { t } from '@/popup/lib/i18n'
import { logError } from '@/shared/logger'
import Tooltip from '@/popup/components/shared/Tooltip/Tooltip.vue'
import { Approval, ApprovalApi, APPROVAL_ACTION } from '@/shared/apis/approvals'
import { Sign } from '@/shared/models/Sign'
import { useUIStore } from '@/popup/store/UIStore'
import { useFixedHeader } from '@/popup/compositions/useFixedHeader'
import ScrollToBottom from '@/popup/components/shared/Scroll/ScrollToBottom.vue'

type Props = {
  approval: Approval<keyof ApprovalApi, Sign>
  origin: string
  message: string
  icon: string
}

type Emits = {
  (
    event: 'on-approval-close',
    action: APPROVAL_ACTION,
    validationFn?: () => void
  ): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SiweSign',
  props: {
    approval: {},
    origin: {},
    message: {},
    icon: {}
  },
  emits: ["on-approval-close"],
  setup(__props: any, { emit: __emit }) {

const emits = __emit
const props = __props
const appStore = useAppStore()
const uiStore = useUIStore()
const currentProfile = appStore.currentProfile
const notification = ref<SiweError>()
const siwe = ref<SiweMessage>()
const validationInterval = ref<number>()
const bottomRef = ref<InstanceType<typeof HTMLElement>>()
const contentRef = ref<InstanceType<typeof HTMLElement>>()
const timerRef = ref()
const scrollOffset = ref<number>(0)
const {
  onContentScroll,
  scrollPercentage,
  isScrollable,
  bottomThresholdOffset,
} = useFixedHeader(scrollOffset, contentRef)

const onClose = async (action: APPROVAL_ACTION) => {
  const validationFn = () => {
    // on submit we want to double check if message didn't expiry/reached valid time
    if (action === 'CONFIRMED') {
      try {
        validateSiweExpirationTime(siwe.value?.expirationTime)
        validateSiweReadyTime(siwe.value?.notBefore)
      } catch (error) {
        if (error instanceof SiweError) {
          return logError(`Error while validating Siwe: ${error.message}`)
        }
      }
    }
  }

  emits('on-approval-close', action, validationFn)
}

// eslint-disable-next-line sonarjs/cognitive-complexity
onMounted(async () => {
  timerRef.value = setTimeout(() => {
    onContentScroll()
  }, 100)
  try {
    await validateSiweMessage(
      props.message,
      props.approval.origin,
      appStore.network.chainId,
      props.approval.requestData.profileAddress,
      uiStore.isPopup,
      props.approval.requestData.href
    )
  } catch (error) {
    if (error instanceof SiweError) {
      notification.value = error
    } else if (error instanceof Error) {
      notification.value = new SiweError(error.message)
    } else {
      notification.value = new SiweError(t('siwe.general_error'))
    }
  }

  try {
    siwe.value = parseSiweMessage(props.message)
  } catch (error) {
    if (error instanceof Error) {
      logError(`Error message while parsing siwe message: ${error.message}`)
    } else {
      logError(`Error while parsing siwe message:`, error)
    }
  }

  // every second we check if message didn't expiry/reached valid time
  validationInterval.value = setInterval(() => {
    try {
      validateSiweExpirationTime(siwe.value?.expirationTime)
      validateSiweReadyTime(siwe.value?.notBefore)

      if (
        notification.value?.message === t('siwe.error.not_valid_yet') ||
        notification.value?.message === t('siwe.error.message_expired')
      ) {
        notification.value = undefined
      }
    } catch (error) {
      if (error instanceof SiweError && !notification.value) {
        notification.value = error
      }
    }
  }, 1000) as unknown as number
})

onUnmounted(() => {
  if (validationInterval.value) {
    clearInterval(validationInterval.value)
  }

  if (timerRef.value) {
    clearInterval(timerRef.value)
  }
})

const scrollToBottom = () => {
  if (bottomRef.value) {
    bottomRef.value.scrollIntoView({ behavior: 'smooth' })
  }
}

const onLinkClick = (link: string) => {
  browser.tabs.create({ url: link })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("section", {
      ref_key: "contentRef",
      ref: contentRef,
      class: "sign-siwe is-flex is-flex-direction-column scrollbar-wrapper",
      onScroll: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onContentScroll) && _unref(onContentScroll)(...args)))
    }, [
      _createVNode(ApprovalHeader, { icon: _ctx.icon }, {
        subtitle: _withCtx(() => [
          _createVNode(Sanitize, {
            html: _unref(truncateMiddle)(_unref(removeSchemaFromUrl)(siwe.value?.domain), 25)
          }, null, 8 /* PROPS */, ["html"])
        ]),
        title: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('sign.siwe_subtitle')), 1 /* TEXT */)
        ]),
        actions: _withCtx(() => [
          _createVNode(IconButton, {
            tooltip: _ctx.$t('sign.cancel'),
            onOnClick: _cache[0] || (_cache[0] = ($event: any) => (onClose('REJECTED')))
          }, null, 8 /* PROPS */, ["tooltip"])
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["icon"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(ProfilePicture, {
            "frontend-profile": _unref(currentProfile),
            size: "medium"
          }, null, 8 /* PROPS */, ["frontend-profile"]),
          _createElementVNode("p", _hoisted_3, [
            _createVNode(UserName, {
              profile: _unref(currentProfile),
              "max-width": 210,
              "has-at-sign": true,
              "has-slice-address": true
            }, null, 8 /* PROPS */, ["profile"])
          ])
        ]),
        (notification.value)
          ? (_openBlock(), _createBlock(Notification, {
              key: 0,
              type: notification.value.type,
              class: "mt-6",
              "data-testid": "notification"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(notification.value.message), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["type"]))
          : _createCommentVNode("v-if", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("span", _hoisted_6, [
            _hoisted_7,
            _createTextVNode(" " + _toDisplayString(_ctx.$t('sign.tag')), 1 /* TEXT */)
          ]),
          _createVNode(Tooltip, {
            value: _ctx.$t('sign.siwe_tag_tooltip'),
            "data-testid": "siwe-tag"
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('sign.siwe_tag')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["value"])
        ]),
        _createElementVNode("div", _hoisted_9, _toDisplayString(siwe.value?.statement), 1 /* TEXT */),
        (siwe.value?.resources?.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t('siwe.resources_title')), 1 /* TEXT */),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(siwe.value.resources, (resource, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "mb-2"
                }, [
                  _createElementVNode("button", {
                    class: "button is-secondary is-fullwidth",
                    onClick: ($event: any) => (onLinkClick(encodeURI(resource))),
                    title: encodeURI(resource),
                    target: "_blank",
                    rel: "noopener noreferer"
                  }, [
                    _hoisted_13,
                    _createTextVNode(" " + _toDisplayString(_unref(truncateMiddle)(_unref(removeSchemaFromUrl)(resource), 18)), 1 /* TEXT */)
                  ], 8 /* PROPS */, _hoisted_12)
                ]))
              }), 128 /* KEYED_FRAGMENT */)),
              _createElementVNode("div", {
                ref_key: "bottomRef",
                ref: bottomRef
              }, null, 512 /* NEED_PATCH */)
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      (_unref(isScrollable))
        ? (_openBlock(), _createBlock(ScrollToBottom, {
            key: 0,
            "show-scroll-button": _unref(bottomThresholdOffset) > 16,
            onScrollToBottom: scrollToBottom
          }, null, 8 /* PROPS */, ["show-scroll-button"]))
        : _createCommentVNode("v-if", true)
    ], 544 /* NEED_HYDRATION, NEED_PATCH */),
    _createElementVNode("div", {
      class: _normalizeClass(["buttons p-4", _unref(isScrollable) && _unref(scrollPercentage) < 85 ? 'shadow' : ''])
    }, [
      _createVNode(Button, {
        variant: "text",
        "is-full-width": true,
        onClick: _cache[2] || (_cache[2] = ($event: any) => (onClose('REJECTED')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('sign.cancel')), 1 /* TEXT */)
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(Button, {
        variant: "primary",
        "is-full-width": true,
        onClick: _cache[3] || (_cache[3] = ($event: any) => (onClose('CONFIRMED'))),
        disabled: notification.value?.type === 'error'
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('sign.confirm_siwe')), 1 /* TEXT */)
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["disabled"])
    ], 2 /* CLASS */)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})